/**
 * Zod schema for the combine groups dialog (#1579 part 3) and its conversion to the
 * API request. The server re-checks every rule; the childcare and overlap rules are
 * server-only.
 */

import { z } from 'zod';
import type { I18n } from '@lingui/core';
import { formatLocalDate, parseDateString } from '@/utils/dateUtils';
import type {
  CombinationSuggestion,
  GroupCombination,
  GroupCombinationRequest,
  IsoWeekday,
} from '@/types/groupCombinations';
import { typeError } from '@/lib/zodTypeError';

/** HH:MM on a 15-minute boundary, 00:00-23:45. */
const QUARTER_HOUR_REGEX = /^([01]\d|2[0-3]):(00|15|30|45)$/;

export const MAX_JOINING_GROUPS = 5;

export const getGroupCombinationSchema = (i18n: I18n) =>
  z
    .object({
      hostGroupId: z.string().min(1, {
        message: i18n._({ id: 'shifts.combination.validation.hostRequired', message: 'Host group is required' }),
      }),
      memberGroupIds: z
        .array(z.string())
        .min(1, {
          message: i18n._({ id: 'shifts.combination.validation.membersRequired', message: 'Select at least one joining group' }),
        })
        .max(MAX_JOINING_GROUPS, {
          message: i18n._({ id: 'shifts.combination.validation.membersMax', message: 'Select at most 5 joining groups' }),
        }),
      weekdays: z.array(z.number().int().min(1).max(7)).min(1, {
        message: i18n._({ id: 'shifts.combination.validation.weekdaysRequired', message: 'Select at least one weekday' }),
      }),
      startTime: z.string().regex(QUARTER_HOUR_REGEX, {
        message: i18n._({ id: 'shifts.combination.validation.quarterHour', message: 'Use a 15-minute step' }),
      }),
      endTime: z.string().regex(QUARTER_HOUR_REGEX, {
        message: i18n._({ id: 'shifts.combination.validation.quarterHour', message: 'Use a 15-minute step' }),
      }),
      validFrom: z.date({
        error: typeError({
          required: i18n._({ id: 'shifts.combination.validation.validFromRequired', message: 'Start date is required' }),
        }),
      }),
      validTo: z.date().optional(),
      note: z.string().max(500, {
        message: i18n._({ id: 'shifts.combination.validation.noteMax', message: 'Note must be at most 500 characters' }),
      }),
    })
    .refine((data) => data.startTime < data.endTime, {
      path: ['endTime'],
      message: i18n._({ id: 'shifts.combination.validation.endAfterStart', message: 'End time must be after start time' }),
    })
    .refine((data) => !data.validTo || data.validTo >= data.validFrom, {
      path: ['validTo'],
      message: i18n._({ id: 'shifts.combination.validation.validToAfterFrom', message: 'End date must not be before start date' }),
    });

export type GroupCombinationFormData = z.infer<ReturnType<typeof getGroupCombinationSchema>>;

/** Form defaults for a new combination, or the values of an existing one. */
export function combinationToFormDefaults(
  combination: GroupCombination | null | undefined,
  defaultValidFrom: Date
): GroupCombinationFormData {
  if (!combination) {
    return {
      hostGroupId: '',
      memberGroupIds: [],
      weekdays: [1, 2, 3, 4, 5],
      startTime: '07:00',
      endTime: '08:00',
      validFrom: defaultValidFrom,
      validTo: undefined,
      note: '',
    };
  }
  return {
    hostGroupId: combination.host_group_id,
    memberGroupIds: [...combination.member_group_ids],
    weekdays: [...combination.weekdays],
    startTime: combination.start_time,
    endTime: combination.end_time,
    validFrom: parseDateString(combination.valid_from) ?? defaultValidFrom,
    validTo: parseDateString(combination.valid_to),
    note: combination.note ?? '',
  };
}

/** Form defaults for a new combination filled in from a suggestion (#1579 part 4). */
export function suggestionToFormDefaults(
  suggestion: CombinationSuggestion,
  validFrom: Date
): GroupCombinationFormData {
  return {
    hostGroupId: suggestion.host_group_id,
    memberGroupIds: [...suggestion.member_group_ids],
    weekdays: [...suggestion.weekdays],
    startTime: suggestion.start,
    endTime: suggestion.end,
    validFrom,
    validTo: undefined,
    note: '',
  };
}

/** Converts the form values to the create request. */
export function toGroupCombinationRequest(data: GroupCombinationFormData): GroupCombinationRequest {
  const note = data.note.trim();
  return {
    host_group_id: data.hostGroupId,
    member_group_ids: data.memberGroupIds,
    // The schema limits every weekday to 1-7.
    weekdays: [...data.weekdays].sort((a, b) => a - b) as IsoWeekday[],
    start_time: data.startTime,
    end_time: data.endTime,
    valid_from: formatLocalDate(data.validFrom),
    valid_to: data.validTo ? formatLocalDate(data.validTo) : undefined,
    note: note === '' ? undefined : note,
  };
}

/** Every 15-minute time of a day, "00:00" ... "23:45". */
export const QUARTER_HOUR_OPTIONS: string[] = Array.from({ length: 96 }, (_, i) => {
  const hours = String(Math.floor(i / 4)).padStart(2, '0');
  const minutes = String((i % 4) * 15).padStart(2, '0');
  return `${hours}:${minutes}`;
});
